import { useEffect, useMemo } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { X, Check, User, Calendar, MessageCircle } from "lucide-react";
import type { ItemAta } from "../../../services/itemAta.service";
import type { Desenvolvimento } from "../../../services/desenvolvimento.service";

interface TopicHistoryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  topicTitle: string;
  items: ItemAta[];
  desenvolvimentos: Desenvolvimento[];
}

export function TopicHistoryDrawer({
  open,
  onOpenChange,
  topicTitle,
  items,
  desenvolvimentos,
}: TopicHistoryDrawerProps) {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
      };
    }
  }, [open]);

  const itemsWithDevs = useMemo(() => {
    return items.map((item) => ({
      item,
      desenvolvimentos: desenvolvimentos
        .filter((d) => d.item_id === item.id)
        .sort((a, b) => a.ata_numero - b.ata_numero),
    }));
  }, [items, desenvolvimentos]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    try {
      const date = dateStr.includes("T") ? dateStr : dateStr + "T00:00:00";
      return format(new Date(date), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const totalItems = items.length;
  const completedItems = items.filter((i) => i.concluido).length;
  const pendingItems = totalItems - completedItems;

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-60 fade-in"
        style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        onClick={() => onOpenChange(false)}
      />

      {/* Drawer */}
      <aside
        className="w-full md:w-[480px] border-l border-border flex flex-col bg-background fixed right-0 top-0 bottom-0 z-70"
        style={{
          height: "100vh",
          animation: "slideInRight 300ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b border-border flex items-center justify-between flex-shrink-0">
          <div className="flex-1 min-w-0 pr-4">
            <p className="text-xs font-medium text-muted-foreground mb-0.5">Histórico do tópico</p>
            <h3 className="font-semibold text-foreground truncate">{topicTitle}</h3>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="p-1.5 hover:bg-muted rounded-md transition-colors flex-shrink-0"
            aria-label="Fechar"
          >
            <X size={18} className="text-muted-foreground" />
          </button>
        </div>

        {/* Stats */}
        <div className="px-4 py-2.5 border-b border-border flex items-center gap-6 flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold text-foreground">{totalItems}</span>
              <span className="text-xs text-muted-foreground">itens</span>
            </div>
            <div className="w-px h-4 bg-border" />
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-emerald-500" />
              <span className="text-sm font-medium text-foreground">{completedItems}</span>
              <span className="text-xs text-muted-foreground">concluídos</span>
            </div>
            <div className="w-px h-4 bg-border" />
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full bg-amber-400" />
              <span className="text-sm font-medium text-foreground">{pendingItems}</span>
              <span className="text-xs text-muted-foreground">pendentes</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {itemsWithDevs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <MessageCircle size={32} className="mb-2" />
              <p className="text-sm">Nenhum item neste tópico</p>
            </div>
          ) : (
            <div className="p-4 space-y-3">
              {itemsWithDevs.map(({ item, desenvolvimentos: devs }) => (
                <div
                  key={item.id}
                  className="rounded-lg border border-border bg-card"
                >
                  {/* Item Header */}
                  <div className="p-3 border-b border-border">
                    <div className="flex items-start gap-2.5">
                      {/* Status */}
                      <div
                        className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${
                          item.concluido
                            ? "bg-emerald-500"
                            : "border-2 border-border"
                        }`}
                      >
                        {item.concluido && <Check size={10} className="text-white" strokeWidth={3} />}
                      </div>

                      <div className="flex-1 min-w-0">
                        {/* Description */}
                        <p
                          className={`text-sm leading-snug break-words ${
                            item.concluido ? "text-muted-foreground line-through" : "text-foreground"
                          }`}
                        >
                          {item.descricao}
                        </p>

                        {/* Meta */}
                        {(item.entidade || item.prazo) && (
                          <div className="flex flex-wrap items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                            {item.entidade && (
                              <span className="flex items-center gap-1">
                                <User size={10} />
                                {item.entidade}
                              </span>
                            )}
                            {item.prazo && (
                              <span className="flex items-center gap-1">
                                <Calendar size={10} />
                                {formatDate(item.prazo)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="p-3 bg-muted/20">
                    {/* Origem */}
                    <div className="flex gap-2.5">
                      <div className="flex flex-col items-center">
                        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 mt-1.5" />
                        {(devs.length > 0 || item.concluido) && (
                          <div className="w-px flex-1 bg-border min-h-[8px]" />
                        )}
                      </div>
                      <div className="flex-1 pb-2.5">
                        <p className="text-xs text-muted-foreground">
                          Criado na Ata #{item.ata_origem_numero || "?"} · {formatDate(item.ata_origem_data) || "—"}
                        </p>
                      </div>
                    </div>

                    {/* Desenvolvimentos */}
                    {devs.map((dev, devIndex) => (
                      <div key={dev.id} className="flex gap-2.5">
                        <div className="flex flex-col items-center">
                          <div className="w-1.5 h-1.5 rounded-full mt-1.5 bg-blue-500" />
                          {(devIndex < devs.length - 1 || item.concluido) && (
                            <div className="w-px flex-1 bg-border min-h-[8px]" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0 pb-2.5">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                              Ata #{dev.ata_numero || '-'}
                            </span>
                            {dev.criado_em && (
                              <span className="text-xs text-muted-foreground">
                                {formatDate(dev.criado_em)}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line" style={{ wordBreak: "break-word", overflowWrap: "break-word" }}>
                            {dev.texto}
                          </p>
                        </div>
                      </div>
                    ))}

                    {/* Concluído */}
                    {item.concluido && (
                      <div className="flex gap-2.5">
                        <div className="flex flex-col items-center">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-medium text-emerald-600">Item concluído</p>
                        </div>
                      </div>
                    )}

                    {/* Empty state */}
                    {devs.length === 0 && !item.concluido && (
                      <p className="text-xs text-muted-foreground italic">Sem atualizações</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
